import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { PopulationDensityHistoricalChart } from '@/components/Charts/PopulationDensityHistorical';
import { GraphFact } from '@/components/Charts/types';
import { getBenchmarksFromFacts } from '@/pages/shared/hooks/useBenchmarkForFact';

import { NoPlotData } from '../NoPlotData';

export const PopulationDensityGraphTile = ({
  graphData,
  benchmarkFact,
}: {
  graphData: GraphFact[] | null | undefined;
  benchmarkFact: {
    urbanCenter: PlotReportFactElement<GraphFact[] | null> | undefined;
    urbanCluster: PlotReportFactElement<GraphFact[] | null> | undefined;
    rural: PlotReportFactElement<GraphFact[] | null> | undefined;
  };
}) => {
  const { t } = useTranslation();
  const { zoom, setZoom } = useChartZoom();

  const noChartData = !graphData?.length;

  if (noChartData) {
    return <NoPlotData />;
  }

  const urbanCenterBenchmarkData = (benchmarkFact.urbanCenter?.value ?? []).map((fact) => {
    return { ...fact, name: `${fact.name}_urban_center` };
  });

  const urbanClusterBenchmarkData = (benchmarkFact.urbanCluster?.value ?? []).map((fact) => {
    return { ...fact, name: `${fact.name}_urban_cluster` };
  });

  const ruralBenchmarkData = (benchmarkFact.rural?.value ?? []).map((fact) => {
    return { ...fact, name: `${fact.name}_rural` };
  });

  const chartData = [...graphData, ...urbanCenterBenchmarkData, ...urbanClusterBenchmarkData, ...ruralBenchmarkData];

  const benchmarks = getBenchmarksFromFacts({
    highBenchmarkFact: benchmarkFact.urbanCenter,
    moderateBenchmarkFact: benchmarkFact.urbanCluster,
    lowBenchmarkFact: benchmarkFact.rural,
  });

  return (
    <Stack
      spacing={6}
      className='items-center justify-center px-1 py-6 lg:items-start lg:p-6 lg:pl-1'
      data-testid='population-density-graph-tile'
    >
      <ChartHeading
        heading={t('shared.ncaDetail.details.populationDensity.labels.historicalGraphHeadline')}
        popover={{
          title: t('shared.ncaDetail.details.populationDensity.explainers.populationDensity.title'),
          body: t('shared.ncaDetail.details.populationDensity.explainers.populationDensity.body'),
        }}
        className='w-full px-6 sm:px-0'
        zoom={zoom}
        setZoom={setZoom}
      />
      <PopulationDensityHistoricalChart data={chartData} zoom={zoom} setZoom={setZoom} benchmarks={benchmarks} />
    </Stack>
  );
};
